import {
  Fragment,
  createElement,
  type ElementType,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils/cn";
import type { HtmlNode } from "@/features/submissions/types/underwriting-questions.types";

export interface SafeHtmlProps {
  nodes: HtmlNode[];
  as?: ElementType;
  className?: string;
}

function renderNode(node: HtmlNode, key: string): ReactNode {
  if (node.type === "text") {
    return <Fragment key={key}>{node.value}</Fragment>;
  }

  const children = node.children.map((child, index) =>
    renderNode(child, `${key}-${index}`),
  );

  switch (node.tag) {
    case "p":
      return (
        <p key={key} className="mb-2 last:mb-0">
          {children}
        </p>
      );
    case "br":
      return <br key={key} />;
    case "ul":
      return (
        <ul key={key} className="list-disc space-y-1 pl-5">
          {children}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="list-decimal space-y-1 pl-5">
          {children}
        </ol>
      );
    case "li":
      return <li key={key}>{children}</li>;
    case "strong":
    case "b":
      return <strong key={key}>{children}</strong>;
    case "em":
    case "i":
      return <em key={key}>{children}</em>;
    case "a": {
      const href = node.attributes?.href;
      if (!href) {
        return (
          <span
            key={key}
            className="cursor-not-allowed text-gray-500 underline decoration-dotted underline-offset-2"
            title="Carrier link unavailable"
            aria-disabled="true"
          >
            {children}
          </span>
        );
      }

      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 underline underline-offset-2 hover:text-blue-800"
        >
          {children}
        </a>
      );
    }
  }
}

export function SafeHtml({
  nodes,
  as = "div",
  className,
}: SafeHtmlProps) {
  return createElement(
    as,
    { className: cn("space-y-1", className) },
    nodes.map((node, index) => renderNode(node, String(index))),
  );
}
